// src/shared/changelog/WhatsNewBoundary.tsx
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useAppSelector } from "@redux/hooks";
import { canSeeChangelog } from "@shared/config/featureFlags";
import WhatsNewModal from "./components/WhatsNewModal";
import { recordAutoOpen } from "./autoOpenStore";
import { useAutoOpenWhatsNew } from "./useAutoOpenWhatsNew";

/**
 * The What's new dialog, held where it can survive the app loading (CTX-592).
 *
 * ── Why it is not in the SideMenu footer ────────────────────────────────────
 *
 * It was, and the dialog flickered and vanished. `SideMenuBody` renders
 * `SideMenuSkeleton` while the profile is loading and the real rail afterwards
 * — two different trees, so the whole footer below it is unmounted and rebuilt
 * the moment the profile lands. The auto-open fired on the first instance,
 * spent the release's one turn on `lastAutoOpenAt`, and died with it; the
 * second instance then read that record and correctly declined to open. The
 * reader saw a dialog for about a second and never saw that release again.
 *
 * So the state lives at `AppLayout`, above everything that swaps — the same
 * place, and for the same reason, as the walkthrough tour's pending launch
 * (`WalkthroughTourProvider`): *the dialog outlives the component that opens
 * it*. The card keeps only what is genuinely the card's — its tint.
 *
 * It mounts **inside** `WalkthroughTourBoundary`, because the modal's
 * "Walk me through it" launches a tour through that provider's context.
 *
 * ── Why the gate is a value and not a branch ────────────────────────────────
 *
 * `canSeeChangelog` is asked here, but it must not decide whether this subtree
 * exists: `tenantType` arrives with the profile, so a branch on it would
 * unmount the host exactly once per load — the bug above, rebuilt. Today the
 * answer happens not to change (outside production the helper ignores
 * `tenantType` entirely, and inside it `isChangelogEnabled()` is false), but
 * CTX-235 flips that, and the shape should not be waiting for it.
 */
const OpenWhatsNewContext = createContext<(() => void) | null>(null);

export function WhatsNewBoundary({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { TenantType: tenantType } = useAppSelector((state) => state.userProfile);
  const isEnabled = canSeeChangelog(tenantType);

  const [isOpen, setIsOpen] = useState(false);

  /**
   * The release this opening is announcing, when the app opened it by itself —
   * `null` for a click.
   *
   * Held until the dialog is actually on screen, because that is when the
   * release's one turn is spent (§2.5). Recording at the moment of *deciding*
   * is what made the dialog flicker and vanish: anything that unmounted this
   * subtree in between kept the spent turn and lost the announcement.
   */
  const pendingAutoRelease = useRef<string | null>(null);

  const open = useCallback(() => {
    // A click is not an auto-open and never costs a turn (§2.1).
    pendingAutoRelease.current = null;
    setIsOpen(true);
  }, []);

  const autoOpen = useCallback((releaseId: string) => {
    pendingAutoRelease.current = releaseId;
    setIsOpen(true);
  }, []);

  /** The dialog reached the screen — now the turn is genuinely spent. */
  const handleShown = useCallback(() => {
    const releaseId = pendingAutoRelease.current;
    if (!releaseId) return;
    pendingAutoRelease.current = null;
    recordAutoOpen(releaseId);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  // The hook does its own nothing when disabled — including not fetching the
  // feed, which is what the footer's gate used to buy.
  useAutoOpenWhatsNew(autoOpen, isEnabled);

  // `null` where the changelog is hidden, so `useOpenWhatsNew` hands its caller
  // a no-op rather than a button that opens a dialog that will not render.
  const opener = isEnabled ? open : null;

  return (
    <OpenWhatsNewContext.Provider value={opener}>
      {children}
      {isEnabled && (
        <WhatsNewModal isOpen={isOpen} onClose={close} onShown={handleShown} />
      )}
    </OpenWhatsNewContext.Provider>
  );
}

/**
 * Opens the What's new dialog — what the footer card's click does.
 *
 * No-ops outside a `WhatsNewBoundary`, like `useLaunchWalkthrough`: a caller
 * should not have to know whether this tenant has the changelog, and the card's
 * own gate at the footer already decides whether it renders at all.
 */
export function useOpenWhatsNew(): () => void {
  const open = useContext(OpenWhatsNewContext);
  return useMemo(() => open ?? (() => undefined), [open]);
}
